import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {PermissionSet} from "../../_auth/Models/PermissionSet";
import {PermissionService} from "../../_auth/permission.service";
import {GlobalAlertService} from "../../_services/global-alert.service";
import {AlertLevel} from "../../enums/alert-level";

@Component({
  selector: 'layout-form-container',
  imports: [
    FormsModule,
    ReactiveFormsModule
  ],
  templateUrl: './form-container.component.html',
  styleUrl: './form-container.component.scss'
})
export class FormContainerComponent implements OnChanges, OnInit {
  @ViewChild("search") search: ElementRef | undefined;
  @Input() hasSubmit: boolean = true;
  @Output() submit = new EventEmitter();
  @Input() submitText: string = "";
  @Output() clear = new EventEmitter();
  @Input() clearText: string = "";
  @Input() preventClearOnCollapseChange: boolean = false;
  @Input() isCollapsed: boolean = true;
  @Output() isCollapsedChange = new EventEmitter<boolean>();
  @Input() hasSearch: boolean = true;
  @Input() searchValue: string = "";
  @Output() searchValueChange = new EventEmitter<string>();
  @Input() permission: PermissionSet | undefined;
  @Input() element: HTMLInputElement | undefined;
  /**
   * Blocks the submit while the form itself knows the call cannot succeed (#664: an event window no contract
   * allows anything in).
   *
   * Disabled rather than hidden: the reason lives next to the fields that caused it, and a button that
   * vanishes leaves the reader looking for the one they were about to press. The host stays responsible for
   * refusing in its own handler too — this is a rendering decision, not the rule.
   */
  @Input() submitDisabled: boolean = false;
  @Output() load = new EventEmitter();

  constructor(private ps: PermissionService,
              private gas: GlobalAlertService) {
  }

  ngOnInit(): void {
    this.load.emit();
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'k') {
      event.preventDefault();
      // @ts-ignore
      this.search?.nativeElement.focus();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.element !== undefined && changes.element) {
      this.element.addEventListener("keyup", (e) => {
        if (this.isCollapsed || this.element === undefined) return;
        this.onSearchChange(this.element.value);
      })

      this.element.addEventListener("keydown", (e) => {
        e.key === "Escape" && this.setCollapsed(true);
        setTimeout(() => {
          this.search?.nativeElement.focus();
        })
      })
    }
  }

  protected onSubmit(e: UIEvent) {
    e.preventDefault();
    if (!this.hasPermission()) return;
    // A `disabled` attribute already stops a pointer/keyboard submit; this covers the programmatic path.
    if (this.submitDisabled && !this.isCollapsed) return;

    if (!this.isCollapsed) {
      if (this.element !== undefined && this.element.value == '') {
        this.gas.createAndShow("Fehler", "Feld darf nicht leer sein.", AlertLevel.error);
        return;
      }
      this.submit.emit();
    } else {
      this.setCollapsed(false)
    }
  }

  protected setCollapsed(value: boolean) {
    if (!value) {
      if (this.element !== undefined) {
        this.element.value = this.searchValue;
        this.element.dispatchEvent(new Event('input'));
      }
    }

    if ((this.element === undefined && !this.preventClearOnCollapseChange) || value) {
      this.onSearchChange("");
    }

    this.isCollapsed = value;
    this.isCollapsedChange.emit(this.isCollapsed);

    if (!this.isCollapsed) {
      this.load.emit();
    }

    setTimeout(() => {
      this.element?.focus();
    })
  }

  protected collapse(e: UIEvent) {
    e.preventDefault();
    this.setCollapsed(true);
  }

  protected onSearchChange(value: string) {
    this.searchValue = value;
    this.searchValueChange.emit(this.searchValue);
  }

  protected hasPermission(): boolean {
    if (!this.hasSubmit) return false;

    if (!this.permission) return true;
    return this.ps.CheckPermission(this.permission);
  }

  protected preventDefault($event: Event) {
    $event.preventDefault();
  }
}
